import { useEffect, useRef, useState } from "react";

import { renderFrameCanvas } from "../editor/framePdf";

import type { MutableRefObject } from "react";
import type {
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
} from "@excalidraw/excalidraw/element/types";
import type { BinaryFiles } from "@excalidraw/excalidraw/types";

/** Rendered frames kept at once: the current one and two on each side. */
export const KEEP_RADIUS = 2;
export const MAX_IMAGE_PX = 2048;

const toBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
      "image/png",
    ),
  );

export type FrameImagesSource = {
  /** Every element, deleted ones included (the renderer filters them). */
  elements: readonly ExcalidrawElement[];
  /** The frames to show, in the order they are shown. */
  frames: readonly ExcalidrawFrameLikeElement[];
  /** Files the renderer draws from. Owned by the caller: with `lazy` set, the
   * hook fills it and evicts what the nearby frames no longer draw; without
   * it, it is handed over ready and left alone. */
  filesRef: MutableRefObject<BinaryFiles>;
  /** Stored images fetched on demand, per frame. */
  lazy?: {
    fileIdsOf(frame: ExcalidrawFrameLikeElement): string[];
    load(
      fileIds: readonly string[],
      files: BinaryFiles,
      isCancelled: () => boolean,
    ): Promise<void>;
  };
};

/**
 * Renders the current frame first, then its neighbours, and keeps only a few
 * images so a long canvas does not fill a device's memory. Shared by the
 * phone viewer and the slideshow.
 */
export const useFrameImages = (
  source: FrameImagesSource | null,
  current: number,
) => {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [failedFrames, setFailedFrames] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const urlsRef = useRef(new Map<string, string>());
  const pendingRef = useRef(new Set<string>());
  const currentRef = useRef(0);
  const mountedRef = useRef(true);
  const sourceRef = useRef(source);
  sourceRef.current = source;

  useEffect(() => {
    mountedRef.current = true;
    const urlMap = urlsRef.current;
    return () => {
      mountedRef.current = false;
      for (const url of urlMap.values()) {
        URL.revokeObjectURL(url);
      }
      urlMap.clear();
    };
  }, []);

  const elements = source?.elements;
  const frames = source?.frames;

  useEffect(() => {
    const active = sourceRef.current;
    if (!active || active.frames.length === 0) {
      return;
    }
    const { frames: shown, elements: all, filesRef, lazy } = active;
    currentRef.current = current;
    const near = (index: number) => Math.abs(index - currentRef.current) <= 1;
    const keep = new Set(
      shown
        .filter((_, index) => Math.abs(index - current) <= KEEP_RADIUS)
        .map((frame) => frame.id),
    );
    let evicted = false;
    for (const [id, url] of urlsRef.current) {
      if (!keep.has(id)) {
        URL.revokeObjectURL(url);
        urlsRef.current.delete(id);
        evicted = true;
      }
    }
    if (evicted) {
      setUrls(Object.fromEntries(urlsRef.current));
    }
    if (lazy) {
      // Downloaded images are the larger cost: keep only those the nearby
      // frames draw.
      const keptFiles = new Set(
        shown
          .filter((frame) => keep.has(frame.id))
          .flatMap((frame) => lazy.fileIdsOf(frame)),
      );
      for (const id of Object.keys(filesRef.current)) {
        if (!keptFiles.has(id)) {
          delete filesRef.current[id];
        }
      }
    }

    const longSide = Math.min(
      MAX_IMAGE_PX,
      Math.round(
        Math.max(window.innerWidth, window.innerHeight) *
          (window.devicePixelRatio || 1),
      ),
    );
    const wanted = [current, current + 1, current - 1].filter(
      (index) => index >= 0 && index < shown.length,
    );
    void (async () => {
      for (const index of wanted) {
        const frame = shown[index];
        if (
          !mountedRef.current ||
          !near(index) ||
          urlsRef.current.has(frame.id) ||
          pendingRef.current.has(frame.id)
        ) {
          continue;
        }
        pendingRef.current.add(frame.id);
        try {
          await lazy?.load(
            lazy.fileIdsOf(frame),
            filesRef.current,
            () => !mountedRef.current,
          );
          const canvas = await renderFrameCanvas(
            {
              elements: all,
              appState: { viewBackgroundColor: "#ffffff" },
              files: filesRef.current,
            },
            frame,
            longSide,
          );
          const blob = await toBlob(canvas);
          canvas.width = 0;
          canvas.height = 0;
          if (mountedRef.current && near(index)) {
            urlsRef.current.set(frame.id, URL.createObjectURL(blob));
            setUrls(Object.fromEntries(urlsRef.current));
          }
        } catch {
          if (mountedRef.current) {
            setFailedFrames((currentSet) => new Set(currentSet).add(frame.id));
          }
        } finally {
          pendingRef.current.delete(frame.id);
        }
      }
    })();
  }, [elements, frames, current]);

  return { urls, failedFrames };
};
